import { useEffect, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";

import { getRegisteredApiHosts } from "@degaus/shared/brand-config";

import type { AuthBrand } from "@/common/product-brand";
import { getApiBaseUrl } from "@/common/api";
import { getSessionToken } from "@/common/auth";
import { getAuthBrand } from "@/common/product-brand";
import { deviceNoun, useDetectedOs } from "@/features/wonda/osInstall";
import { api } from "@/trpc/react";

// Registry-driven: wonda's hosts (api.wondercat.ai, staging.api.wondercat.ai)
// come from the shared product registry; future products add theirs there.
const ALLOWED_API_HOSTS = [...getRegisteredApiHosts(), "localhost"];

function resolveExchangeUrl(apiUrl?: string): string {
  if (!apiUrl) return `${getApiBaseUrl()}/v1/auth/cli-exchange`;

  // In dev mode, allow any host (ngrok, etc.)
  if (import.meta.env.DEV) {
    return `${apiUrl}/api/v1/auth/cli-exchange`;
  }

  try {
    const host = new URL(apiUrl).hostname;
    if (ALLOWED_API_HOSTS.includes(host)) {
      return `${apiUrl}/api/v1/auth/cli-exchange`;
    }
  } catch {
    // invalid URL, fall through
  }

  // Reject unknown hosts — use default API
  return `${getApiBaseUrl()}/v1/auth/cli-exchange`;
}

const cliAuthSearchSchema = z.object({
  port: z.coerce.number().min(1024).max(65535).optional(),
  token: z.string().optional(),
  api_url: z.string().optional(),
  kind: z.enum(["cli", "relay"]).optional(),
  device_name: z.string().optional(),
  product: z.string().optional(),
});

export const Route = createFileRoute("/_authenticated/(wonda)/_wonda/cli-auth")(
  {
    validateSearch: zodValidator(cliAuthSearchSchema),
    component: CliAuth,
  },
);

function CliAuth() {
  const { port, token, api_url, kind, device_name, product } =
    Route.useSearch();
  const authBrand = getAuthBrand(product);

  if (token) {
    return (
      <TokenFlow
        token={token}
        apiUrl={api_url}
        kind={kind ?? "cli"}
        deviceName={device_name}
        authBrand={authBrand}
      />
    );
  }

  if (port) {
    return <LegacyPortFlow port={port} authBrand={authBrand} />;
  }

  return (
    <CenterScreen>
      <h1 className="text-xl font-semibold">Invalid authentication request</h1>
      <p className="text-muted-foreground mt-2">
        Missing token or port parameter. Please try again from the CLI.
      </p>
    </CenterScreen>
  );
}

function TokenFlow({
  token,
  apiUrl,
  kind,
  deviceName,
  authBrand,
}: {
  token: string;
  apiUrl?: string;
  kind: "cli" | "relay";
  deviceName?: string;
  authBrand: AuthBrand;
}) {
  const { brand, logoSrc } = authBrand;
  // This page is what `relay pair` opens, so it is the first thing a Windows
  // user sees after installing. It said "Mac linked" to them.
  const noun = deviceNoun(useDetectedOs());
  const exchangedRef = useRef(false);
  const [status, setStatus] = useState<"exchanging" | "done" | "error">(
    "exchanging",
  );
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (exchangedRef.current) return;
    exchangedRef.current = true;

    async function tryExchange(skipTokenCache: boolean) {
      const sessionToken = await getSessionToken(
        skipTokenCache ? { skipCache: true } : undefined,
      );
      const exchangeUrl = resolveExchangeUrl(apiUrl);
      const res = await fetch(exchangeUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionToken}`,
        },
        body: JSON.stringify({ token, kind, deviceName }),
      });

      if (!res.ok) {
        let message = "Authentication failed";
        try {
          const body = (await res.json()) as {
            error?: { message?: string };
          };
          if (body.error?.message) message = body.error.message;
        } catch {
          // use default message
        }
        throw new Error(message);
      }
    }

    // Retry with backoff — handles the race between signup and account
    // provisioning (the bearer may not resolve the account until the row
    // lands and the token refreshes).
    async function exchangeWithRetry() {
      const maxAttempts = 5;
      const baseDelayMs = 1500;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          await tryExchange(attempt > 0);
          return;
        } catch (err) {
          if (attempt === maxAttempts - 1) throw err;
          await new Promise((resolve) =>
            setTimeout(resolve, baseDelayMs * Math.pow(2, attempt)),
          );
        }
      }
    }

    exchangeWithRetry()
      .then(() => setStatus("done"))
      .catch((err: Error) => {
        setStatus("error");
        setErrorMessage(err.message);
      });
  }, [token, apiUrl, kind, deviceName]);

  if (status === "error") {
    return (
      <CenterScreen>
        <h1 className="text-xl font-semibold">Authentication failed</h1>
        <p className="text-muted-foreground mt-2">{errorMessage}</p>
      </CenterScreen>
    );
  }

  if (status === "done") {
    return (
      <CenterScreen>
        <div className="bg-muted mx-auto flex h-20 w-20 items-center justify-center rounded-2xl border">
          <img src={logoSrc} alt={brand.productName} className="h-12 w-12" />
        </div>
        <h1 className="mt-4 text-xl font-semibold">
          {kind === "relay" ? `${noun} linked` : "Authenticated!"}
        </h1>
        <p className="text-muted-foreground mt-2">
          {kind === "relay"
            ? `Your local ${brand.displayName} relay is paired. Continue in setup to connect accounts and add Claude.`
            : "You can close this tab and return to your terminal."}
        </p>
      </CenterScreen>
    );
  }

  return (
    <CenterScreen>
      <div className="bg-muted mx-auto flex h-20 w-20 items-center justify-center rounded-2xl border">
        <img src={logoSrc} alt={brand.productName} className="h-12 w-12" />
      </div>
      <h1 className="mt-4 text-xl font-semibold">
        {kind === "relay"
          ? `Linking this ${noun}...`
          : `Connecting to ${brand.productName} CLI...`}
      </h1>
      <p className="text-muted-foreground mt-2">
        {kind === "relay"
          ? "Creating a scoped relay credential for this device."
          : "Sending your API key to the terminal."}
      </p>
    </CenterScreen>
  );
}

function LegacyPortFlow({
  port,
  authBrand,
}: {
  port: number;
  authBrand: AuthBrand;
}) {
  const { brand, logoSrc } = authBrand;
  const redirectedRef = useRef(false);
  const [authenticated, setAuthenticated] = useState(false);

  const { data, isLoading, error } = api.account.getApiKeyForCli.useQuery();

  useEffect(() => {
    if (redirectedRef.current || !data?.apiKey) return;
    redirectedRef.current = true;

    fetch(`http://localhost:${port}/callback`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ key: data.apiKey, email: data.email }),
    })
      .then((res) => {
        if (!res.ok) throw new Error("CLI server error");
        setAuthenticated(true);
      })
      .catch(() => {
        window.location.href = `http://localhost:${port}/callback?key=${encodeURIComponent(data.apiKey)}&email=${encodeURIComponent(data.email)}`;
      });
  }, [data, port]);

  if (error) {
    return (
      <CenterScreen>
        <h1 className="text-xl font-semibold">Authentication failed</h1>
        <p className="text-muted-foreground mt-2">{error.message}</p>
      </CenterScreen>
    );
  }

  if (authenticated) {
    return (
      <CenterScreen>
        <div className="bg-muted mx-auto flex h-20 w-20 items-center justify-center rounded-2xl border">
          <img src={logoSrc} alt={brand.productName} className="h-12 w-12" />
        </div>
        <h1 className="mt-4 text-xl font-semibold">Authenticated!</h1>
        <p className="text-muted-foreground mt-2">
          You can close this tab and return to your terminal.
        </p>
      </CenterScreen>
    );
  }

  return (
    <CenterScreen>
      <div className="bg-muted mx-auto flex h-20 w-20 items-center justify-center rounded-2xl border">
        <img src={logoSrc} alt={brand.productName} className="h-12 w-12" />
      </div>
      <h1 className="mt-4 text-xl font-semibold">
        {isLoading || !data
          ? `Connecting to ${brand.productName} CLI...`
          : "Sending key to CLI..."}
      </h1>
      <p className="text-muted-foreground mt-2">
        {isLoading || !data
          ? "Sending your API key to the terminal."
          : "You can close this tab after the terminal confirms authentication."}
      </p>
    </CenterScreen>
  );
}

function CenterScreen({ children }: { children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 flex items-center justify-center">
      <div className="text-center">{children}</div>
    </div>
  );
}
